/**
 * THE WIRE between the simulation's animation state and the animation
 * controller — one instance per drawn player.
 *
 * The flow this file completes, end to end:
 *
 *     sim AnimState → RenderState.anim → AnimCommand → PlayerAnimator → clip
 *
 * Before it existed, `RenderState.anim` was serialised, carried across the seam
 * (context.ts says out loud it was enriched for exactly this) and then dropped:
 * `playerVisual` derived locomotion from velocity alone and `playAction` had no
 * caller outside the perf harness. That is not "animation not wired yet" — it is
 * a SECOND state machine racing the authoritative one. The sim could resolve a
 * strike, move the ball and set `AnimState.Pass` while the renderer, looking
 * only at how fast the capsule was travelling, played a run cycle. Both were
 * internally consistent and neither was right.
 *
 * WHO DECIDES WHAT (the rule, restated where it is enforced):
 *   - the SIM decides discrete actions. A pass happens because `anim` says so.
 *   - VELOCITY may refine playback only: rate, which forward gait, lean.
 * `PlayerAnimator.setLocomotionCommand` holds the second half; this file holds
 * the first — specifically the EDGE detection, because an action is an event and
 * `anim` is a level. The sim holds `AnimState.Pass` for a whole strike window;
 * re-requesting the action on every one of those frames would restart the clip
 * every frame and the player would twitch on the spot forever.
 */
import {
  AnimCommand, commandForAnimState, isOneShotCommand,
} from './animationCommand';
import { PlayerAnimator } from './playerAnimator';

/** Ground-truth motion for the frame, derived from the render transforms. */
export interface AnimKinematics {
  /** Horizontal ground speed, m/s. */
  speed: number;
  /** Travelling against the facing. */
  reversing: boolean;
  /** Sideways component, +left. */
  lateral: number;
  /**
   * This slot has the ball at its feet, i.e. `ball.possessedBy === this id`
   * (RenderState.ball.possessedBy). Same authority tier as `reversing`/
   * `lateral` above — it refines WHICH forward gait plays (Dribble vs. Jog/
   * Run), never a discrete action, so it does not compete with `anim`.
   * Optional so an existing caller that hasn't threaded it degrades to the
   * plain ladder instead of failing to compile.
   */
  dribbling?: boolean;
}

/**
 * Sustained states that OUTRANK a one-shot already on screen.
 *
 * A player who is knocked over stops passing, and he stops immediately — the
 * sim's own priority order in `stepAnim` puts `Fallen` above every strike for
 * the same reason. Everything else lets the swing finish: the sim holds a strike
 * state for only ~0.22 s (KICK_ANIM_HOLD_S) while the clip that draws it runs
 * well over a second, so cancelling the moment the state lapses would show the
 * wind-up of every pass and the contact of none.
 */
function overridesAction(cmd: AnimCommand): boolean {
  return cmd === AnimCommand.FALL || cmd === AnimCommand.STUMBLE;
}

/**
 * Actions whose clip IS the whole arc — the move plus its own recovery back
 * to a standing man at the origin, sized exactly to the sim's committed
 * window. While one of these is on the body, the states the sim steps
 * through underneath (SlideRecover's GET_UP, the bicycle kick's Fallen tick)
 * must not cut it: the slide would snap to a face-down push-up mid-slide,
 * and the scissor kick would be cancelled ONE TICK in by its own knockdown.
 * The owner's brief, twice: "there should always be continuation."
 */
function ownsItsRecovery(cmd: AnimCommand | null): boolean {
  return cmd === AnimCommand.SLIDE || cmd === AnimCommand.BICYCLE_KICK
    || cmd === AnimCommand.GK_DIVE_LEFT || cmd === AnimCommand.GK_DIVE_RIGHT
    || cmd === AnimCommand.GK_SMOTHER;
}

export class AnimationDirector {
  /** Last command seen from the sim. Edge detection is against THIS, not the clip. */
  private last: AnimCommand | null = null;
  /**
   * The execution id (§11) the current command arrived with — the OTHER half of
   * the edge test, and the half `last` cannot supply.
   *
   * `null` means "no execution has been observed yet" (a fresh director, or one
   * reset onto a recycled slot). It is deliberately not 0: 0 is a legitimate
   * execution id for a player who has never acted, and treating it as "nothing"
   * would swallow that player's first action.
   */
  private lastExec: number | null = null;

  constructor(readonly animator: PlayerAnimator) {}

  /** What the sim last asked for. For the debug overlay only. */
  get command(): AnimCommand | null {
    return this.last;
  }

  /** The execution id currently on screen, or null. Debug/diagnostics only. */
  get execId(): number | null {
    return this.lastExec;
  }

  /**
   * Feed one frame of simulation state. Call every RENDERED frame, before
   * `advance` — locomotion is a level and must track even on frames where the
   * mixer is throttled out.
   *
   * @param anim the raw `AnimState` off `RenderState.anim`. Nothing else in the
   *   renderer may read that field; it is translated here, once.
   * @param execId the sim's `animExecId` off `RenderState.animExec` (§11). The
   *   IDENTITY of the action currently showing. Compared for inequality only.
   */
  drive(anim: number, execId: number, k: AnimKinematics): void {
    const cmd = commandForAnimState(anim);

    // THE EDGE IS THE EXECUTION ID, NOT THE COMMAND VALUE.
    //
    // Both halves are needed and each covers a hole the other leaves:
    //
    //   - the id alone would fire on a locomotion transition (Run → Idle stamps
    //     a new execution), which must not restart a one-shot;
    //   - the command alone cannot see a RE-ARM. `AnimState.Pass` written over
    //     `AnimState.Pass` is a second accepted pass with no value edge, and the
    //     previous wiring rendered it as one continuous pass — the wind-up of
    //     the first, the contact of neither. That is exactly the ambiguity §11
    //     exists to remove.
    //
    // So: a NEW EXECUTION is an edge whatever the command; the same execution is
    // never an edge, even if a frame is replayed. A command change without an id
    // change cannot happen from a live sim (every anim write stamps), but it is
    // still honoured, because a harness feeding `anim` with a constant id is a
    // legitimate caller and must animate.
    const first = this.lastExec === null;
    const edge = first || execId !== this.lastExec || cmd !== this.last;
    this.last = cmd;
    this.lastExec = execId;

    // The base layer runs unconditionally — including underneath a one-shot,
    // whose suppression holds the gait's WEIGHT at zero while its phase keeps
    // advancing. That is what lets locomotion resume mid-stride afterwards
    // instead of snapping to frame 0.
    this.animator.setLocomotionCommand(cmd, k.speed, k.reversing, k.lateral, k.dribbling ?? false);

    if (!edge) return;

    if (isOneShotCommand(cmd)) {
      // THE SLIDE OWNS ITS RECOVERY. The slide clip is the whole reference
      // arc — the slide plus the take's own rise back to his feet, 1.300 s =
      // SLIDE_TACKLE_SLIDE_S + SLIDE_TACKLE_RECOVERY_S — so the GET_UP that
      // SlideRecover raises at 0.5 s must not cut it into the prone
      // wake-up. The owner rejected exactly that cut twice ("there should
      // always be continuation", "told you to remove that prev animation of
      // waking up"): a man sliding on his side has no business snapping to a
      // face-down push-up. The clip ends standing at the origin on the same
      // tick the sim returns his controls, so nothing is owed at the far end
      // either. GET_UP still serves every knockdown — FallForward ends prone
      // and its edge arrives with no slide on the body.
      if (cmd === AnimCommand.GET_UP
          && ownsItsRecovery(this.animator.activeCommand)
          && this.animator.actionBusy) {
        return;
      }
      // A NEW discrete action from an authoritative source always wins, even
      // over one already playing: if the sim says he is sliding now, he is
      // sliding now. `requestAction` cross-ramps the two so the total weight
      // never dips (two actions requested on adjacent frames is a real case —
      // a tackle into a clearance).
      this.animator.requestAction(cmd, { interrupt: true });
      return;
    }

    // A sustained state arrived while a one-shot was mid-flight. Cancel only if
    // it outranks the swing; otherwise let the follow-through play out. A
    // self-recovering arc outranks even FALL: the bicycle kick's own Fallen
    // state arrives one tick after the strike, and cancelling the kick with
    // the knockdown it scheduled for itself is how "press the button, watch a
    // forward fall" shipped.
    if (this.animator.actionBusy && overridesAction(cmd)
        && !ownsItsRecovery(this.animator.activeCommand)) {
      this.animator.stopAction();
    }
  }

  /**
   * Advance the mixer by `dt` and report a BALL CONTACT if this is the frame the
   * running action strikes.
   *
   * The beat comes from the action's own entry in `ACTION_CONTACT_BEAT`, which
   * is read out of the simulation's `ACTION_DEFS` — one table, so the visible
   * contact and the impulse cannot drift apart. Fires at most once per action
   * (the controller holds the token), which is the entire point: without it a
   * caller hanging an effect off "the contact frame is on screen" fires it on
   * every frame the frame is on screen.
   *
   * @returns the command that made contact, or null.
   */
  advance(dt: number): AnimCommand | null {
    const cmd = this.animator.activeCommand;
    this.animator.update(dt);
    if (cmd === null) return null;
    return this.animator.consumeActionEvent() ? cmd : null;
  }

  /**
   * The body is no longer this player's — a slot recycled onto someone else.
   *
   * Hard reset rather than a ramp: there is nobody to hand the body back to, and
   * a leftover one-shot weight would bias the first frame the next footballer is
   * drawn on.
   */
  reset(): void {
    this.last = null;
    // Cleared to null, NOT to a sentinel number: the next footballer in this
    // slot may legitimately arrive carrying the same execution id the last one
    // left behind (the counters are per-player and the wire token wraps), and
    // comparing against a stale number would swallow his first action.
    this.lastExec = null;
    this.animator.resetAction();
  }
}
